import { lazy, StrictMode, Suspense } from 'react';
import './i18n';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Logo } from './components/ocean';
import { useLang } from './lib/i18n';
import { ToastProvider } from './components/ui';
import { ApiKeysPage } from './pages/ApiKeys';
import { AuthPage } from './pages/Auth';
import { DomainDetailPage, DomainsPage } from './pages/Domains';
import { EmailDetailPage, EmailsPage } from './pages/Emails';
import { Onboarding } from './pages/Onboarding';
import { OverviewPage } from './pages/Overview';
import { AcceptInvitePage, ForgotPasswordPage, ResetPasswordPage } from './pages/Recovery';
import { TeamPage } from './pages/Team';
import { AuditPage } from './pages/Audit';
import { SecurityPage } from './pages/Security';
import { TemplateEditorPage, TemplatesPage } from './pages/Templates';
import { SettingsPage } from './pages/Settings';
import { SuppressionsPage } from './pages/Suppressions';
import { WebhooksPage } from './pages/Webhooks';
import { DetectivePage } from './pages/Detective';
import { Code2EmailPage } from './pages/Code2Email';
import { BillingPage, InvoicePage } from './pages/Billing';
import { ContactsPage } from './pages/Contacts';
import { ContactDetailPage } from './pages/ContactDetail';
import { EventsPage } from './pages/Events';
import { SegmentEditorPage, SegmentsPage } from './pages/Segments';
import { JourneysPage } from './pages/journeys/Journeys';
import { JourneyPage } from './pages/journeys/JourneyPage';
// Build 4 «Пилот» loads on first visit: the main bundle stays as it was.
const PilotPage = lazy(() => import('./pages/pilot/Pilot').then((m) => ({ default: m.PilotPage })));
const PilotThreadPage = lazy(() => import('./pages/pilot/PilotThread').then((m) => ({ default: m.PilotThreadPage })));
const PilotVocabularyPage = lazy(() => import('./pages/pilot/Vocabulary').then((m) => ({ default: m.PilotVocabularyPage })));
// Build 5 «Каналы» load on first visit as well.
const ChannelsHomePage = lazy(() => import('./pages/channels/ChannelsHome').then((m) => ({ default: m.ChannelsHomePage })));
const ChannelPage = lazy(() => import('./pages/channels/ChannelPage').then((m) => ({ default: m.ChannelPage })));
const PoliciesPage = lazy(() => import('./pages/channels/Policies').then((m) => ({ default: m.PoliciesPage })));
const PolicyPage = lazy(() => import('./pages/channels/Policies').then((m) => ({ default: m.PolicyPage })));
const ChannelTemplatesPage = lazy(() => import('./pages/channels/Templates').then((m) => ({ default: m.ChannelTemplatesPage })));
const ChannelTemplatePage = lazy(() => import('./pages/channels/Templates').then((m) => ({ default: m.ChannelTemplatePage })));
const ChannelCostsPage = lazy(() => import('./pages/channels/CostsSettings').then((m) => ({ default: m.ChannelCostsPage })));
const ChannelSettingsPage = lazy(() => import('./pages/channels/CostsSettings').then((m) => ({ default: m.ChannelSettingsPage })));
const NotificationsPage = lazy(() => import('./pages/notifications/Notifications').then((m) => ({ default: m.NotificationsPage })));
const NotificationPage = lazy(() => import('./pages/notifications/Notifications').then((m) => ({ default: m.NotificationPage })));
const lazyPage = (el: JSX.Element) => <Suspense fallback={<div className="skeleton" style={{ height: 240 }} />}>{el}</Suspense>;
import { captureFirstTouch } from './lib/activity';
import { applyTheme, readTheme, SessionProvider, useSession } from './lib/session';
import { canSetUpWorkspace, needsOnboarding } from './lib/api';
import './styles/app.css';
import './styles/pilot.css';

applyTheme(readTheme());
// First touch (UTM, external referrer, landing page) before anyone signs in; sent with the sign-up form.
captureFirstTouch();

function Splash() {
  return (
    <div className="ocean-screen" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ animation: 'bob 2.4s ease-in-out infinite' }}>
        <Logo size={64} />
      </div>
    </div>
  );
}

function RequireAuth() {
  const { me, loading } = useSession();
  const loc = useLocation();
  if (loading) return <Splash />;
  if (!me) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (me.workspace.require_2fa && !me.user.mfa_enabled && loc.pathname !== '/security') return <Navigate to="/security" replace />;
  return <Outlet />;
}

/** Owner/admin pages: other roles go to the overview instead of seeing a 403. */
function AdminOnly({ children }: { children: JSX.Element }) {
  const { me } = useSession();
  return me && canSetUpWorkspace(me) ? children : <Navigate to="/" replace />;
}

function GuestOnly() {
  const { me, loading } = useSession();
  if (loading) return <Splash />;
  if (me) return <Navigate to={needsOnboarding(me) ? '/onboarding' : '/'} replace />;
  return <Outlet />;
}

function Root() {
  const lang = useLang();
  return (
    <ToastProvider>
      <SessionProvider>
        <BrowserRouter key={lang}>
          <Routes>
            <Route element={<GuestOnly />}>
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/signup" element={<AuthPage mode="signup" />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            </Route>
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/invite" element={<AcceptInvitePage />} />
            <Route element={<RequireAuth />}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route element={<Layout />}>
                <Route index element={<OverviewPage />} />
                <Route path="/emails" element={<EmailsPage />} />
                <Route path="/emails/:id" element={<EmailDetailPage />} />
                <Route path="/domains" element={<DomainsPage />} />
                <Route path="/domains/:id" element={<DomainDetailPage />} />
                <Route path="/api-keys" element={<ApiKeysPage />} />
                <Route path="/webhooks" element={<WebhooksPage />} />
                <Route path="/suppressions" element={<SuppressionsPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/contacts/:id" element={<ContactDetailPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/segments" element={<SegmentsPage />} />
                <Route path="/segments/:key" element={<SegmentEditorPage />} />
                <Route path="/pilot" element={lazyPage(<PilotPage />)} />
                <Route path="/pilot/vocabulary" element={lazyPage(<PilotVocabularyPage />)} />
                <Route path="/pilot/:threadId" element={lazyPage(<PilotThreadPage />)} />
                <Route path="/journeys" element={<JourneysPage />} />
                <Route path="/journeys/:id" element={<JourneyPage />} />
                <Route path="/channels" element={lazyPage(<ChannelsHomePage />)} />
                <Route path="/channels/policies" element={lazyPage(<PoliciesPage />)} />
                <Route path="/channels/policies/:key" element={lazyPage(<PolicyPage />)} />
                <Route path="/channels/templates" element={lazyPage(<ChannelTemplatesPage />)} />
                <Route path="/channels/templates/:key" element={lazyPage(<ChannelTemplatePage />)} />
                <Route path="/channels/costs" element={<AdminOnly>{lazyPage(<ChannelCostsPage />)}</AdminOnly>} />
                <Route path="/channels/settings" element={lazyPage(<ChannelSettingsPage />)} />
                <Route path="/channels/:slug" element={lazyPage(<ChannelPage />)} />
                <Route path="/notifications" element={lazyPage(<NotificationsPage />)} />
                <Route path="/notifications/:id" element={lazyPage(<NotificationPage />)} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/templates/generate" element={<Code2EmailPage />} />
                <Route path="/detective" element={<DetectivePage />} />
                <Route path="/templates/:id" element={<TemplateEditorPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/audit" element={<AdminOnly><AuditPage /></AdminOnly>} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/billing/invoices/:id" element={<AdminOnly><InvoicePage /></AdminOnly>} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </SessionProvider>
    </ToastProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
